import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import type { Post, CommunicationChannel } from '../../../../types';
import { defaultChannels } from '../../../../config/channels';
import { SortType } from '../../../common/SortSelector';
import { PostStatus, PostStatusFilter } from '../../../../utils/Constants';
import { useAuthContext } from '../../../../contexts/AuthContext';

// V4-FRONT-ANY-USAGE batch 6 2026-05-29 — shape exact des contrôles exposés au
// header mobile (cf. l'objet construit dans le useEffect plus bas). Partagé avec
// DashboardPage qui stocke ces contrôles en state.
export interface MobileHeaderControls {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  sortType: SortType;
  sortOrder: 'asc' | 'desc';
  onSortTypeChange: (type: SortType) => void;
  onSortOrderChange: (order: 'asc' | 'desc') => void;
  statusFilter?: PostStatusFilter;
  onStatusFilterChange?: (s: PostStatusFilter) => void;
  onCreatePost: () => void;
  onGeneratePosts?: () => void;
  onClearGallery: () => void;
  availableChannels: CommunicationChannel[];
  selectedChannelIds: Set<string>;
  onToggleChannel: (channelId: string) => void;
  showExternal: boolean;
  showInternal: boolean;
  onToggleExternal: () => void;
  onToggleInternal: () => void;
  // V4-POSTS-CREATED-BY-USER-ID 2026-07-29 — filtre « mes posts »
  showOnlyMine: boolean;
  onToggleOnlyMine: () => void;
  canFilterMine: boolean;
  startDate: string;
  endDate: string;
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
  onCreateComplete?: (post: Post) => void;
  onGenerateComplete: (posts: Post[]) => void;
}

interface UsePostFiltersProps {
  posts: Post[];
  communicationChannels: CommunicationChannel[];
  isMobile: boolean;
  onHeaderControlsReady?: (controls: MobileHeaderControls) => void;
  onOpenGenerateModal?: () => void;
  onCreatePost?: (post: Post) => void;
}

export function usePostFilters({
  posts,
  communicationChannels,
  isMobile,
  onHeaderControlsReady,
  onOpenGenerateModal,
  onCreatePost
}: UsePostFiltersProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sortType, setSortType] = useState<SortType>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [statusFilter, setStatusFilter] = useState<PostStatusFilter>(PostStatusFilter.ALL);
  const [showExternal, setShowExternal] = useState(true);
  const [showInternal, setShowInternal] = useState(true);
  // V4-POSTS-CREATED-BY-USER-ID 2026-07-29 — « mes posts ». Défaut OFF : la
  // galerie continue de tout montrer tant que l'utilisateur ne demande rien.
  const [showOnlyMine, setShowOnlyMine] = useState(false);
  const [availableChannels, setAvailableChannels] = useState<CommunicationChannel[]>([]);
  const [selectedChannelIds, setSelectedChannelIds] = useState<Set<string>>(new Set());
  const [channelOffset, setChannelOffset] = useState(0);
  const [visibleChannelsCount, setVisibleChannelsCount] = useState(3);
  const [showFilters, setShowFilters] = useState(false);
  const [isCreatingNewPost, setIsCreatingNewPost] = useState(false);
  const [showClearGalleryConfirm, setShowClearGalleryConfirm] = useState(false);

  // V4-FRONT-MOBILE-CONTROLS-STALE-FLAGS 2026-07-29 — invariant de ce hook :
  //   • les CALLBACKS du caller vivent derrière une ref (identité instable) ;
  //   • les VALEURS sont lues directement et déclarées dans les deps de l'effet
  //     d'exposition (plus bas).
  //
  // Avant ce correctif, les valeurs passaient AUSSI par des refs sans figurer
  // dans les deps : l'effet ne se rejouait donc pas quand elles changeaient, et
  // le parent (`DashboardPage`, qui stocke l'objet en state) gardait un snapshot
  // périmé. 9 champs étaient concernés — pas seulement `showExternal`/
  // `showInternal` : `searchQuery`, `sortType`, `sortOrder`, `selectedChannelIds`,
  // `availableChannels`, `startDate`, `endDate` l'étaient tout autant.
  //
  // ⚠️ Ne PAS mettre un callback du caller dans les deps : `onOpenGenerateModal`
  // est une arrow inline côté DashboardPage (identité neuve à chaque render), ce
  // qui ferait boucler l'effet (effet → setState parent → render → nouvelle
  // identité → effet…). C'est la raison d'être des refs — pas les valeurs.
  const onHeaderControlsReadyRef = useRef(onHeaderControlsReady);
  const onOpenGenerateModalRef = useRef(onOpenGenerateModal);
  const onCreatePostRef = useRef(onCreatePost);

  useEffect(() => {
    onHeaderControlsReadyRef.current = onHeaderControlsReady;
    onOpenGenerateModalRef.current = onOpenGenerateModal;
    onCreatePostRef.current = onCreatePost;
  });

  // Initialize channels
  useEffect(() => {
    const enabledChannels = communicationChannels
      .filter(c => c.enabled !== false)
      .sort((a, b) => {
        const aNetwork = a.network || '';
        const bNetwork = b.network || '';
        const networkCompare = aNetwork.localeCompare(bNetwork);
        if (networkCompare !== 0) return networkCompare;
        return (a.customName || aNetwork).localeCompare(b.customName || bNetwork);
      });
    
    setAvailableChannels(enabledChannels);
    setSelectedChannelIds(new Set(enabledChannels.map(c => c.id)));
  }, [communicationChannels]);

  // Calculate visible channels count
  useEffect(() => {
    const calculateVisibleChannels = () => {
      const windowWidth = window.innerWidth;
      const sidebarWidth = 64;
      const rightPadding = 32;
      const externInterneWidth = 200;
      const navigationWidth = 80;
      const dateFilterWidth = 300;
      const gaps = 16;
      
      const availableWidthForChannels = windowWidth - sidebarWidth - rightPadding - externInterneWidth - navigationWidth - dateFilterWidth - (gaps * 3);
      const avgChannelItemWidth = 135;
      const maxVisibleChannels = Math.max(1, Math.floor(availableWidthForChannels / avgChannelItemWidth));
      
      setVisibleChannelsCount(Math.min(maxVisibleChannels, 6));
    };

    calculateVisibleChannels();
    window.addEventListener('resize', calculateVisibleChannels);
    return () => window.removeEventListener('resize', calculateVisibleChannels);
  }, []);

  const toggleChannel = useCallback((channelId: string) => {
    setSelectedChannelIds(prevSet => {
      const newSet = new Set(prevSet);
      if (newSet.has(channelId)) {
        newSet.delete(channelId);
      } else {
        newSet.add(channelId);
      }
      return newSet;
    });
  }, []);

  const handleToggleExternal = useCallback(() => {
    setShowExternal(prev => !prev);
  }, []);

  const handleToggleInternal = useCallback(() => {
    setShowInternal(prev => !prev);
  }, []);

  // V4-POSTS-CREATED-BY-USER-ID 2026-07-29 — filtre « mes posts ».
  // `createdByUserId` est un audit trail (qui a cliqué « générer »), PAS de
  // l'ownership : dans un space d'agence plusieurs membres écrivent dans la même
  // galerie, et c'est exactement ce cas que ce filtre sert.
  //
  // ⚠️ Les posts créés AVANT le 2026-05-28 n'ont pas le champ (undefined) et
  // sont donc masqués quand le filtre est actif — le backfill
  // (`scripts/maintenance/backfill_created_by_user_id.py`) sert à ça. On préfère
  // masquer un post au statut incertain plutôt que l'attribuer à tort.
  const { user } = useAuthContext();
  const currentUserId = user?.id;
  // Sans identité connue, le filtre n'a aucun sens : le masquer plutôt que
  // d'offrir un bouton qui viderait la galerie.
  const canFilterMine = Boolean(currentUserId);

  const handleToggleOnlyMine = useCallback(() => {
    setShowOnlyMine(prev => !prev);
  }, []);

  // Si l'identité disparaît (logout, expiration), on relâche le filtre pour ne
  // pas laisser une galerie vide et inexplicable derrière soi.
  useEffect(() => {
    if (!canFilterMine && showOnlyMine) setShowOnlyMine(false);
  }, [canFilterMine, showOnlyMine]);

  const scrollChannels = (direction: 'left' | 'right') => {
    if (direction === 'left') {
      setChannelOffset(Math.max(0, channelOffset - 1));
    } else {
      setChannelOffset(Math.min(availableChannels.length - visibleChannelsCount, channelOffset + 1));
    }
  };

  const isExternalChannel = (channel: CommunicationChannel): boolean => {
    // 1. Priorité au champ isExternal stocké en BDD
    if (channel.isExternal !== undefined) {
      return channel.isExternal;
    }
    // 2. Vérifier channelType (ChannelCategory enum)
    if (channel.channelType) {
      return channel.channelType === 'external';
    }
    // 3. Fallback : liste de réseaux externes (valeurs enum en minuscules)
    const externalNetworks = ['x', 'twitter', 'facebook', 'instagram', 'linkedin', 'tiktok', 'youtube', 'pinterest', 'baidu', 'threads', 'bluesky'];
    const networkLower = (channel.network || '').toLowerCase();
    return externalNetworks.includes(networkLower);
  };

  const filteredAvailableChannels = useMemo(() => {
    return availableChannels.filter(channel => {
      const isExternal = isExternalChannel(channel);
      if (!showExternal && isExternal) return false;
      if (!showInternal && !isExternal) return false;
      return true;
    });
  }, [availableChannels, showExternal, showInternal]);

  // Wrappers stables des callbacks du caller — ils peuvent entrer dans les deps
  // de l'effet d'exposition sans le faire boucler, contrairement aux callbacks
  // bruts. `useCallback([])` + lecture par ref au moment de l'appel.
  const handleOpenCreatePost = useCallback(() => setIsCreatingNewPost(true), []);
  const handleOpenClearGallery = useCallback(() => setShowClearGalleryConfirm(true), []);
  const handleGeneratePosts = useCallback(() => onOpenGenerateModalRef.current?.(), []);
  const handleCreateComplete = useCallback((post: Post) => onCreatePostRef.current?.(post), []);
  const handleGenerateComplete = useCallback((posts: Post[]) => {
    posts.forEach(post => onCreatePostRef.current?.(post));
  }, []);

  // `MobilePostGalleryHeader` conditionne le rendu de certains blocs à la simple
  // PRÉSENCE de ces callbacks (`{onCreateComplete && …}`). Les wrappers étant
  // toujours définis, on réintroduit l'optionalité d'origine — sinon un caller
  // qui ne passe pas `onCreatePost` verrait apparaître un bloc de création.
  const canCreatePost = Boolean(onCreatePost);
  const canGeneratePosts = Boolean(onOpenGenerateModal);

  // Mobile controls exposure — expose toutes les commandes (search/sort/filter/...)
  // pour que `MobilePostGalleryHeader` (déclenché par le bouton central du
  // MobileBottomBar) puisse les utiliser comme source de vérité unique.
  useEffect(() => {
    if (isMobile && onHeaderControlsReadyRef.current) {
      onHeaderControlsReadyRef.current({
        searchQuery,
        onSearchChange: setSearchQuery,
        sortType,
        sortOrder,
        onSortTypeChange: setSortType,
        onSortOrderChange: setSortOrder,
        // Status filter exposé pour mobile (manquait avant ce fix).
        statusFilter,
        onStatusFilterChange: setStatusFilter,
        onCreatePost: handleOpenCreatePost,
        onGeneratePosts: canGeneratePosts ? handleGeneratePosts : undefined,
        onClearGallery: handleOpenClearGallery,
        availableChannels: filteredAvailableChannels,
        selectedChannelIds,
        onToggleChannel: toggleChannel,
        showExternal,
        showInternal,
        onToggleExternal: handleToggleExternal,
        onToggleInternal: handleToggleInternal,
        showOnlyMine,
        onToggleOnlyMine: handleToggleOnlyMine,
        canFilterMine,
        startDate,
        endDate,
        onStartDateChange: setStartDate,
        onEndDateChange: setEndDate,
        onCreateComplete: canCreatePost ? handleCreateComplete : undefined,
        onGenerateComplete: handleGenerateComplete
      });
    }
    // Toutes les VALEURS exposées ci-dessus sont dans les deps : c'est ce qui
    // garantit que le header mobile reçoit un snapshot à jour. Les setters
    // `useState` et les `useCallback([])` sont d'identité stable, donc l'effet
    // converge : il se rejoue, pousse un objet neuf au parent, et le render
    // suivant ne le redéclenche pas (aucune dep n'a bougé).
  }, [
    isMobile,
    searchQuery, sortType, sortOrder, statusFilter,
    filteredAvailableChannels, selectedChannelIds,
    showExternal, showInternal, showOnlyMine, canFilterMine,
    startDate, endDate,
    canCreatePost, canGeneratePosts,
    toggleChannel, handleToggleExternal, handleToggleInternal, handleToggleOnlyMine,
    handleOpenCreatePost, handleOpenClearGallery, handleGeneratePosts,
    handleCreateComplete, handleGenerateComplete
  ]);

  const filteredPosts = useMemo(() => {
    return posts.filter((post) => {
      // V4-POSTS-CREATED-BY-USER-ID 2026-07-29 — « mes posts ».
      // Comparaison stricte : un post sans `createdByUserId` (antérieur au
      // 2026-05-28 ou non backfillé) n'est PAS considéré comme mien.
      if (showOnlyMine && currentUserId && post.createdByUserId !== currentUserId) {
        return false;
      }

      // Check External/Internal filter first
      if (post.channelId) {
        const postChannel = communicationChannels.find(c => c.id === post.channelId);
        if (postChannel) {
          const isExternal = isExternalChannel(postChannel);
          if (!showExternal && isExternal) return false;
          if (!showInternal && !isExternal) return false;
        }
      }

      // Check if channel is selected
      // If the post's channel is known (exists in communicationChannels), respect the selection.
      // If the post's channel is unknown (orphan), show it by default to avoid hiding data.
      const isKnownChannel = communicationChannels.some(c => c.id === post.channelId);
      if (isKnownChannel && post.channelId && !selectedChannelIds.has(post.channelId)) {
        return false;
      }
      
      if (startDate) {
        const start = new Date(startDate);
        if (post.generatedDate < start) return false;
      }
      
      if (endDate) {
        const end = new Date(endDate);
        if (post.generatedDate > end) return false;
      }
      
      if (searchQuery) {
        const lowerCaseQuery = searchQuery.toLowerCase();
        if (!post.content.toLowerCase().includes(lowerCaseQuery)) return false;
      }

      // Status filter — dropdown "Statut : Tous / À publier / Programmés / Publiés"
      // Note (2026-05) : `post.status` peut être absent sur de vieilles données ;
      // on retombe alors sur la présence de `scheduledDate` pour distinguer
      // draft vs scheduled (ce qui était la convention historique).
      if (statusFilter !== PostStatusFilter.ALL) {
        const isPublished = post.status === PostStatus.PUBLISHED;
        const isScheduled = post.status === PostStatus.SCHEDULED || (!!post.scheduledDate && !isPublished);
        const isDraft = !isPublished && !isScheduled;
        if (statusFilter === PostStatusFilter.PUBLISHED && !isPublished) return false;
        if (statusFilter === PostStatusFilter.SCHEDULED && !isScheduled) return false;
        if (statusFilter === PostStatusFilter.DRAFT && !isDraft) return false;
      }

      return true;
    }).sort((a, b) => {
      let comparison = 0;
      
      if (sortType === 'date') {
        comparison = a.generatedDate.getTime() - b.generatedDate.getTime();
      } else if (sortType === 'channel') {
        const channelA = communicationChannels.find(c => c.id === a.channelId);
        const channelB = communicationChannels.find(c => c.id === b.channelId);
        const labelA = channelA?.customName || channelA?.network || '';
        const labelB = channelB?.customName || channelB?.network || '';
        comparison = labelA.localeCompare(labelB);
      } else if (sortType === 'alphabetical') {
        comparison = a.content.localeCompare(b.content);
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    });
  }, [posts, selectedChannelIds, startDate, endDate, searchQuery, sortType, sortOrder, statusFilter, communicationChannels, showExternal, showInternal, showOnlyMine, currentUserId]);

  const visibleChannels = filteredAvailableChannels.slice(channelOffset, channelOffset + visibleChannelsCount);

  return {
    searchQuery, setSearchQuery,
    startDate, setStartDate,
    endDate, setEndDate,
    sortType, setSortType,
    sortOrder, setSortOrder,
    statusFilter, setStatusFilter,
    showExternal, handleToggleExternal,
    showInternal, handleToggleInternal,
    showOnlyMine, handleToggleOnlyMine, canFilterMine,
    availableChannels,
    selectedChannelIds, toggleChannel,
    channelOffset, scrollChannels,
    visibleChannelsCount,
    showFilters, setShowFilters,
    isCreatingNewPost, setIsCreatingNewPost,
    showClearGalleryConfirm, setShowClearGalleryConfirm,
    filteredAvailableChannels,
    visibleChannels,
    filteredPosts
  };
}
